import * as React from "react";
import { BloomreachApi, parsePageModel, getPageRoot, defaultComponentDefinitions, createConfiguration, BrConfigurationProvider, BrPage, CmsContainerPage } from "@grandvisionhq/bloomreach";
import { useFeatureToggles } from "@grandvisionhq/state";
import { isDefined, LocaleUtils } from "@grandvisionhq/utils";
import { isPreview } from "../../utils.js";
function withBloomreach(settings) {
  return (config) => (PageComponent) => {
    const wrapInContainerPage = isDefined(config?.wrapInContainerPage) ? config?.wrapInContainerPage : true;
    const checkForBloomreachPathOverride = isDefined(config?.checkForBloomreachPathOverride) ? config?.checkForBloomreachPathOverride : false;
    const WithBloomreach = (pageProps) => {
      const {
        pageModel,
        visitor,
        isMultilingualEnabled = false,
        locale,
        isContentPage,
        path
      } = pageProps;
      const {
        featureConfigs: { bloomreachRefPrefix }
      } = useFeatureToggles();
      const { bloomreach, defaultLocale } = settings;
      const componentDefinitions = bloomreach.componentDefinitions ?? defaultComponentDefinitions;
      const configurationSettings = {
        apiUrl: bloomreach.apiUrl,
        componentDefinitions,
        customComponentMap: bloomreach.customComponentMap,
        refPrefix: bloomreachRefPrefix
      };
      const config2 = React.useMemo(
        () => createConfiguration({
          apiUrl: bloomreach.apiUrl,
          path: path || "/",
          options: {
            isMultilingual: isMultilingualEnabled,
            locale: locale ?? defaultLocale,
            visitor,
            refPrefix: bloomreachRefPrefix
          }
        }),
        [
          bloomreach.apiUrl,
          path,
          isMultilingualEnabled,
          locale,
          defaultLocale,
          visitor,
          bloomreachRefPrefix
        ]
      );
      const shouldWrap = !isContentPage && wrapInContainerPage;
      const Wrapper = shouldWrap ? CmsContainerPage : React.Fragment;
      return /* @__PURE__ */ React.createElement(BrConfigurationProvider, { settings: configurationSettings }, /* @__PURE__ */ React.createElement(BrPage, { mapping: componentDefinitions, page: pageModel, configuration: config2 }, /* @__PURE__ */ React.createElement(Wrapper, null, /* @__PURE__ */ React.createElement(PageComponent, { ...pageProps, isContentPage }))));
    };
    if (process.env.NODE_ENV !== "production") {
      const displayName = PageComponent.displayName || PageComponent.name || "Unknown";
      WithBloomreach.displayName = `WithBloomreach(${displayName})`;
    }
    WithBloomreach.getInitialProps = async (ctx) => {
      const {
        req,
        asPath = "",
        res,
        pageModel,
        featureToggles: {
          featureConfigs: { bloomreachRefPrefix }
        }
      } = ctx;
      const { defaultLocale, isMultilingualEnabled } = settings;
      const { apiUrl } = settings.bloomreach;
      const query = req?.query;
      const preview = isPreview(query);
      const normalizeLocale = (value) => {
        if (!value) {
          return void 0;
        }
        const [language, region] = value.replace("_", "-").split("-");
        if (!language || !region) {
          return void 0;
        }
        return `${language}-${region.toUpperCase()}`;
      };
      let locale;
      if (isMultilingualEnabled) {
        if (req) {
          locale = LocaleUtils.getLocale(req, defaultLocale, preview);
        } else {
          locale = normalizeLocale(ctx.locale) ?? normalizeLocale(LocaleUtils.getLocaleFromUrl(asPath)) ?? defaultLocale;
        }
      }
      locale = locale ?? defaultLocale;
      let pageProps = {};
      if (checkForBloomreachPathOverride && PageComponent.getInitialProps) {
        pageProps = {
          ...await PageComponent.getInitialProps(ctx)
        };
      }
      const bloomreachPath = pageProps.bloomreachPathOverride ?? asPath;
      let rehydratedPageModel = res?.locals?.pageModel || pageModel;
      let { visitor } = ctx;
      if (!rehydratedPageModel) {
        try {
          new URL(asPath, apiUrl);
        } catch (error) {
          console.error(`Invalid URL path ${asPath}`, error);
          return { statusCode: 400 };
        }
        try {
          const brPage = await BloomreachApi.getBrPage(apiUrl, bloomreachPath, {
            locale,
            headers: req?.headers,
            connection: req?.connection,
            isMultilingual: isMultilingualEnabled,
            refPrefix: bloomreachRefPrefix
          });
          rehydratedPageModel = parsePageModel(brPage);
          if (!visitor) {
            const v = brPage?.getVisitor();
            if (v) {
              visitor = v;
            }
          }
        } catch (error) {
          console.error("An error occurred while fetching the Bloomreach page:", error);
          return { statusCode: 400 };
        }
      }
      const pageRoot = getPageRoot(rehydratedPageModel, bloomreachRefPrefix);
      const isContentPage = pageRoot?.models?.pageType === "content-page";
      ctx.pageModel = rehydratedPageModel;
      ctx.visitor = visitor;
      ctx.isContentPage = isContentPage;
      ctx.query = query;
      if (!checkForBloomreachPathOverride && PageComponent.getInitialProps) {
        pageProps = {
          isContentPage,
          ...await PageComponent.getInitialProps(ctx)
        };
      }
      if (!rehydratedPageModel) {
        console.error(`WithBloomreach: Failed to fetch page for ${bloomreachPath}`);
      }
      return {
        ...pageProps,
        isContentPage,
        visitor,
        locale,
        path: bloomreachPath,
        isMultilingualEnabled,
        pageModel: rehydratedPageModel
      };
    };
    return WithBloomreach;
  };
}
export {
  withBloomreach
};
//# sourceMappingURL=with-bloomreach.js.map
